import React, { memo } from 'react';
import PropTypes from 'prop-types';

import { Icon } from 'shared-vectors';
import CaretIcon from 'shared-vectors/icons/Caret';

import createStyle from 'shared-utils/src/createStyle';
import makeClassName from 'shared-utils/src/makeClassName';
import formatPublisherAddress from 'shared-utils/src/leads/publisherAddress';
import trackGTMEvents from 'shared-utils/src/trackGTMEvents/new';
import sleep from 'shared-utils/src/sleep';

import Button from '../Button';
import Grid from '../Grid';
import GridItem from '../GridItem';

import style, { mainCls } from './style';

const useStyles = createStyle(style);

const PublisherCard = ({
  device,
  data,
  lastOne,
  leadsUtils,
  className,
  maplist,
}) => {
  const cls = useStyles();

  const isMobile = device === 'smartphone';

  const { openSpecificModalAction, app } = leadsUtils;

  const isSrp = app === 'P18';

  const imageServer = 'https://images-1.casa.it/';

  const {
    publisherId,
    publisherName,
    publisherAddress: { street, town, province, state, postcode },
    publisherUrl,
    publisherLogo,
    hasCrossSubscription,
  } = data;

  const hasPlaceholder = !!(
    publisherLogo.indexOf('agencylogo') > -1 ||
    publisherLogo.indexOf('placeholder') > -1
  );
  const resize = hasCrossSubscription ? '125x60-resize/' : '';
  const logoSrc = `${imageServer}${resize}${publisherLogo}`.replace(
    '//agencylogo',
    '/agencylogo',
  );

  const baseCls = makeClassName([
    cls[mainCls],
    maplist && 'maplist',
    'c-bg--w',
    'mt--m',
    'p--l',
    lastOne ? 'mb--l' : '',
    className,
  ]);

  const imgCntCls = makeClassName([cls[`${mainCls}__logo`]]);
  const nameCls = makeClassName([
    cls[`${mainCls}__name`],
    hasPlaceholder && cls[`${mainCls}__name--nomt`],
    'mb--ls',
    'tp-w--l',
    'tp-s--l',
    'c-txt--f0',
  ]);
  const addressCls = makeClassName([
    cls[`${mainCls}__address`],
    'tp-w--s',
    'tp-s--m',
    'c-txt--secondary-l',
  ]);
  const btnsCls = makeClassName([cls[`${mainCls}__btns`]]);
  const linkCls = makeClassName([
    cls[`${mainCls}__btns__link`],
    !isSrp && !isMobile && 'pdpdsk',
    'mr--m',
  ]);

  const logoAndNameCntCls = makeClassName([cls[`${mainCls}__logo-and-name`]]);
  const linkACls = makeClassName([
    cls[`${mainCls}__btns__link-a`],
    'c-bg--w',
    'tp-w--m',
    'tp-s--l',
    'p--s',
  ]);
  const linkSpanCls = makeClassName([
    isSrp
      ? cls[`${mainCls}__btns__link-span`]
      : cls[`${mainCls}__btns__link-spanpdp`],
    'tp-a--c',
    'tp-w--m',
    !isMobile && 'mr--s',
  ]);

  const contactsBtnCls = makeClassName([
    cls[`${mainCls}__btns__contacts-btn`],
    'modal_handle',
    'tp-w--m',
    'tp-s--l',
    'p--s',
  ]);

  const ghostDivCls = makeClassName([cls[`${mainCls}__btns__ghost`]]);
  const ghostDivClsDskt = makeClassName([cls[`${mainCls}__btns__ghost-dsk`]]);

  const contactModal = () => {
    sleep(300).then(() => trackGTMEvents({
      category: 'Interaction',
      action: 'OpenContactForm',
      position: 'SRP_AgencyCard',
    }, {}, true));
    openSpecificModalAction(data);
  }

  const trackGotoPub = () =>
  sleep(300).then(trackGTMEvents({
      category: 'Interaction',
      action: 'GoToAgencyProfile',
      label: publisherId.toString(),
      position: 'SRP_AgencyCard',
    }, {}, true));

  return (
    <Grid
      direction="row"
      className={baseCls}
      align="center"
      justify="space-between"
    >
      <Grid align="center" justify="center" className={logoAndNameCntCls}>
        {!hasPlaceholder && (
          <div className={imgCntCls}>
            <img
              className={`${cls[`${mainCls}__logo__img`]}${
                hasCrossSubscription
                  ? ` ${cls[`${mainCls}__logo__img--pm`]}`
                  : ` ${cls[`${mainCls}__logo__ img--csa`]}`
              }`}
              src={logoSrc}
              alt={publisherName}
            />
          </div>
        )}

        <GridItem className={`${cls[`${mainCls}__title-address`]} mr--s`}>
          <p className={nameCls}>{publisherName}</p>
          <p className={addressCls}>
            {formatPublisherAddress({
              street,
              town,
              province,
              state,
              postcode,
            })}
          </p>
        </GridItem>
        <GridItem className={ghostDivClsDskt} flex />
      </Grid>
      <Grid className={btnsCls}>
        <GridItem className={linkCls} behavior="fixed">
          <Button
            className={linkACls}
            border="f2-s"
            color="f0"
            bg="0"
            size="0"
            action={publisherUrl}
            title={`Vedi tutti gli immobili dell'agenzia ${publisherName}`}
            onClick={trackGotoPub}
            target={maplist ? '_blank' : null}
          >
            <span className={linkSpanCls}>
              {isSrp || isMobile
                ? "Vedi tutti gli immobili dell'agenzia"
                : 'Vedi gli immobili'}
            </span>
            <div className={`${mainCls}__btns__link-arrow-cnt`}>
              <Icon
                name={CaretIcon}
                className={`${mainCls}__btns__link-arrow`}
                width="14px"
              />
            </div>
          </Button>
        </GridItem>
        <GridItem className={ghostDivClsDskt} flex />
        <Grid className={ghostDivCls} flex />
        <GridItem behavior="fixed">
          <Button
            border="f2-s"
            color="f0"
            bg="0"
            size="0"
            className={contactsBtnCls}
            data-action="agencyLead"
            action={contactModal}
          >
            <span className="cnt" style={device !== '' ? { opacity: 1 } : null}>
              {`Contatta${maplist ? ' agenzia' : ''}`}
            </span>
          </Button>
        </GridItem>
      </Grid>
    </Grid>
  );
};

const deviceCheck = (prevProps, nextProps) =>
  prevProps.device === nextProps.device;

export default memo(PublisherCard, deviceCheck);

PublisherCard.propTypes = {
  device: PropTypes.string,
  data: PropTypes.instanceOf(Object),
  lastOne: PropTypes.bool,
  leadsUtils: PropTypes.instanceOf(Object),
  className: PropTypes.string,
  maplist: PropTypes.bool,
};

PublisherCard.defaultProps = {
  device: '',
  data: {},
  lastOne: false,
  leadsUtils: {},
  className: '',
  maplist: false,
};
